/**
 * Core calculation engine for Scenario (scenario.solar).
 *
 * All functions are pure — no side effects, no React hooks.
 * Input/output types are strict interfaces.
 */

import type { ConfigId, MonthlyMetrics, PlanRates, UserConfig } from '@/types'
import { DAYS_IN_MONTH, SELF_CONS_SOLAR_ONLY, BATTERY_USABLE_FRAC, BATTERY_ROUND_TRIP } from '@/data/constants'
import { calcFreeOverlap } from './freeWindowOverlap'
import { getSeasonalFactors } from './seasonalFactors'

/**
 * Calculate all monthly metrics for a given plan, config, and month.
 */
export const calculateMonthlyMetrics = (
  monthIndex: number,
  config: UserConfig,
  planRates: PlanRates,
  configId: ConfigId,
  locMult: number,
  locPriceMult: number,
  simAdjust: number
): MonthlyMetrics => {
  const { loadFactor, solarFactor } = getSeasonalFactors(monthIndex)
  const isSolar = configId !== 'raw' && config.useSolar !== false
  const isBattery = configId === 'solar_bat' && config.useSolar !== false

  const effectiveSolarFactor = config.hourlySimulation ? (1 + simAdjust / 100) : solarFactor
  const psh = 4.0 * effectiveSolarFactor
  const potentialGenKwh =
    (isSolar ? config.pvSize : 0) *
    psh *
    DAYS_IN_MONTH *
    (config.pvGeneration / 100) *
    (1 - config.systemLosses / 100) *
    locMult

  // Consumption
  let baseMonthlyCons = config.consumption || 1200
  if (config.consMode === 'daily') baseMonthlyCons *= DAYS_IN_MONTH
  else if (config.consMode === 'weekly') baseMonthlyCons *= 4.345
  else if (config.consMode === 'annual') baseMonthlyCons /= 12

  const monthlyConsKwh = baseMonthlyCons * loadFactor

  // 3-tier consumption
  let peakConsKwh = monthlyConsKwh * (config.consPeakFrac ?? 0.25)
  let shoulderConsKwh = monthlyConsKwh * (config.consShoulderFrac ?? 0.1)
  let nightConsKwh = monthlyConsKwh * (config.consNightFrac ?? 0.65)

  // EV charging
  let monthlyEvKwh = 0
  if (config.useEv) {
    const distance = config.evDistance || 0
    if (config.evDistMode === 'monthly') {
      monthlyEvKwh = (distance / 100) * config.evEfficiency
    } else if (config.evDistMode === 'weekly') {
      monthlyEvKwh = (distance / 100) * config.evEfficiency * 4.345
    } else {
      monthlyEvKwh = (distance / 100) * config.evEfficiency * DAYS_IN_MONTH
    }

    if (config.evChargeProfile === 'night') {
      nightConsKwh += monthlyEvKwh
    } else if (config.evChargeProfile === 'shoulder') {
      shoulderConsKwh += monthlyEvKwh
    } else {
      peakConsKwh += monthlyEvKwh
    }
  }

  const dayConsKwh = peakConsKwh + shoulderConsKwh

  // Solar self-consumption (direct)
  const dailyUsableCap = config.batteryCapacity * BATTERY_USABLE_FRAC
  const selfConsRate = SELF_CONS_SOLAR_ONLY
  const daySelfUse = Math.min(dayConsKwh, potentialGenKwh * selfConsRate)

  // Battery storage
  const dailyGen = potentialGenKwh / DAYS_IN_MONTH
  const dailyDaySelfUse = daySelfUse / DAYS_IN_MONTH
  const dailySurplus = Math.max(0, dailyGen - dailyDaySelfUse / BATTERY_USABLE_FRAC)

  const dailyStoredKwh = Math.min(dailyUsableCap, dailySurplus * BATTERY_ROUND_TRIP)
  const monthlyStoredKwh = dailyStoredKwh * DAYS_IN_MONTH

  // Battery discharge
  const remainingDayConsKwh = Math.max(0, dayConsKwh - daySelfUse)
  const batDayUse =
    isBattery && config.useEss ? Math.min(remainingDayConsKwh, monthlyStoredKwh) : 0
  const remainingMonthlyStoredKwh = monthlyStoredKwh - batDayUse
  const batNightUse =
    isBattery && config.useEss ? Math.min(nightConsKwh, remainingMonthlyStoredKwh) : 0
  const batSelfUse = batDayUse + batNightUse

  const totalSelfConsumption = daySelfUse + batSelfUse / BATTERY_ROUND_TRIP

  // Export/clipping
  const peakGenKw = (dailyGen / 5) * 1.25
  const exportPowerCap = config.exportLimit
  const avgDayHouseLoadKw = dailyDaySelfUse / 10
  const excessPower = Math.max(0, peakGenKw - (exportPowerCap + avgDayHouseLoadKw))
  const clippingLossKwhPerDay = excessPower > 0 ? excessPower * 0.5 * 3 : 0
  const monthlyClippingLossKwh = Math.min(
    potentialGenKwh * 0.15,
    clippingLossKwhPerDay * DAYS_IN_MONTH
  )

  const potentialExportKwh = Math.max(0, potentialGenKwh - totalSelfConsumption)

  // Export FiT weighting
  const ePeakFrac = config.exportPeakFrac ?? 0.15
  const eShoulderFrac = config.exportShoulderFrac ?? 0.0
  const eOffPeakFrac = config.exportOffPeakFrac ?? 0.85

  const wkdayPeakFit = Math.abs(planRates.peakFit || 0)
  const wkdayShoulderFit = Math.abs(planRates.shoulderFit ?? planRates.peakFit ?? 0)
  const wkdayOffPeakFit = Math.abs(planRates.offPeakFit || 0)

  const wkndPeakFit = Math.abs(planRates.weekendPeakFit ?? wkdayPeakFit)
  const wkndShoulderFit = Math.abs(planRates.weekendShoulderFit ?? planRates.weekendPeakFit ?? wkdayShoulderFit)
  const wkndOffPeakFit = Math.abs(planRates.weekendOffPeakFit ?? wkdayOffPeakFit)

  const blendedPeakFit = wkdayPeakFit * (5 / 7) + wkndPeakFit * (2 / 7)
  const blendedShoulderFit = wkdayShoulderFit * (5 / 7) + wkndShoulderFit * (2 / 7)
  const blendedOffPeakFit = wkdayOffPeakFit * (5 / 7) + wkndOffPeakFit * (2 / 7)

  const baseFit = blendedPeakFit * ePeakFrac + blendedShoulderFit * eShoulderFrac + blendedOffPeakFit * eOffPeakFrac
  const weightedFit = baseFit * (1 - config.exportCurtailment / 100)

  const exportKwh = Math.max(0, potentialExportKwh - monthlyClippingLossKwh)

  // 3-tier retail rates (weekday base)
  let retailPeakRate = (planRates.peakRate || 0.3) * locPriceMult
  let retailShoulderRate = (planRates.shoulderRate || planRates.peakRate || 0.3) * locPriceMult
  let retailNightRate = (planRates.nightRate || 0.15) * locPriceMult
  const monthlyFixedCharge = planRates.fixedCharge * DAYS_IN_MONTH * locPriceMult

  // Apply free power window discount per tier (WEEKDAY portion)
  let wkdayPeakRate = retailPeakRate
  let wkdayShoulderRate = retailShoulderRate
  let wkdayNightRate = retailNightRate

  let wkdayPeakDisc = 0
  let wkdayShoulderDisc = 0
  let wkdayNightDisc = 0

  const fws =
    planRates.freeWindows ||
    ('freeWindow' in planRates && (planRates as Record<string, unknown>).freeWindow
      ? [(planRates as Record<string, unknown>).freeWindow]
      : [])

  for (const fw of fws as Array<{ enabled: boolean; days: string; startHour: number; endHour: number; discount?: number }>) {
    if (!fw || !fw.enabled) continue
    const dayFrac = fw.days === 'weekdays' ? 1.0 : fw.days === 'weekends' ? 0.0 : 1.0
    const discFrac = (fw.discount !== undefined ? fw.discount : 100) / 100
    if (dayFrac > 0) {
      wkdayPeakDisc +=
        calcFreeOverlap(fw.startHour, fw.endHour, [
          [7, 9],
          [17, 21],
        ], 6) *
        dayFrac *
        discFrac
      wkdayShoulderDisc +=
        calcFreeOverlap(fw.startHour, fw.endHour, [[9, 17]], 8) * dayFrac * discFrac
      wkdayNightDisc +=
        calcFreeOverlap(
          fw.startHour,
          fw.endHour,
          [
            [21, 24],
            [0, 7],
          ],
          10
        ) *
        dayFrac *
        discFrac
    }
  }
  wkdayPeakRate *= Math.max(0, 1 - wkdayPeakDisc)
  wkdayShoulderRate *= Math.max(0, 1 - wkdayShoulderDisc)
  wkdayNightRate *= Math.max(0, 1 - wkdayNightDisc)

  // Weekend rate override
  let wkndPeakRate =
    (planRates.weekendPeakRate != null ? planRates.weekendPeakRate : planRates.peakRate) *
    locPriceMult
  let wkndShoulderRate =
    (planRates.weekendShoulderRate != null
      ? planRates.weekendShoulderRate
      : planRates.shoulderRate || planRates.peakRate) * locPriceMult
  let wkndNightRate =
    (planRates.weekendNightRate != null ? planRates.weekendNightRate : planRates.nightRate) *
    locPriceMult

  let wkndPeakDisc = 0
  let wkndShoulderDisc = 0
  let wkndNightDisc = 0

  // Apply free power window discount per tier (WEEKEND portion)
  for (const fw of fws as Array<{ enabled: boolean; days: string; startHour: number; endHour: number; discount?: number }>) {
    if (!fw || !fw.enabled) continue
    const dayFrac = fw.days === 'weekdays' ? 0.0 : fw.days === 'weekends' ? 1.0 : 1.0
    const discFrac = (fw.discount !== undefined ? fw.discount : 100) / 100
    if (dayFrac > 0) {
      wkndPeakDisc +=
        calcFreeOverlap(fw.startHour, fw.endHour, [
          [7, 9],
          [17, 21],
        ], 6) *
        dayFrac *
        discFrac
      wkndShoulderDisc +=
        calcFreeOverlap(fw.startHour, fw.endHour, [[9, 17]], 8) * dayFrac * discFrac
      wkndNightDisc +=
        calcFreeOverlap(
          fw.startHour,
          fw.endHour,
          [
            [21, 24],
            [0, 7],
          ],
          10
        ) *
        dayFrac *
        discFrac
    }
  }
  wkndPeakRate *= Math.max(0, 1 - wkndPeakDisc)
  wkndShoulderRate *= Math.max(0, 1 - wkndShoulderDisc)
  wkndNightRate *= Math.max(0, 1 - wkndNightDisc)

  // Blend: 5/7 weekday + 2/7 weekend
  retailPeakRate = wkdayPeakRate * (5 / 7) + wkndPeakRate * (2 / 7)
  retailShoulderRate = wkdayShoulderRate * (5 / 7) + wkndShoulderRate * (2 / 7)
  retailNightRate = wkdayNightRate * (5 / 7) + wkndNightRate * (2 / 7)

  // Grid consumption
  const gridDayKwh = Math.max(0, dayConsKwh - daySelfUse - batDayUse)
  const gridNightKwh = Math.max(0, nightConsKwh - batNightUse)

  const llfMult = 1 + config.lineLossFactor / 100

  // Cost calculation
  //
  // When evChargeProfile === 'shoulder' (Free Window), the user charges
  // exclusively during the free window period (e.g. weekends).  The EV load
  // was added to shoulderConsKwh which is priced at the blended
  // weekday/weekend rate, but it should only be priced at the weekend
  // shoulder rate.  We correct for this after computing the base cost.
  const costBeforeSolarRaw =
    (peakConsKwh * retailPeakRate +
      shoulderConsKwh * retailShoulderRate +
      nightConsKwh * retailNightRate) *
      llfMult +
    monthlyFixedCharge

  let evCostAdjustment = 0
  if (config.useEv && config.evChargeProfile === 'shoulder') {
    // Remove the blended shoulder cost for EV kWh, replace with weekend-only rate
    evCostAdjustment = monthlyEvKwh * (retailShoulderRate - wkndShoulderRate) * llfMult
  }
  const costBeforeSolar = costBeforeSolarRaw - evCostAdjustment

  // Solar self-use savings
  const sPeakFrac = config.solarPeakFrac ?? 0.2
  const sShoulderFrac = config.solarShoulderFrac ?? 0.8
  const solarSavingsRate = retailPeakRate * sPeakFrac + retailShoulderRate * sShoulderFrac
  
  // Battery day discharge offsets actual day load, not the solar generation curve
  const dayLoadPeakFrac = dayConsKwh > 0 ? peakConsKwh / dayConsKwh : 0
  const dayLoadShoulderFrac = dayConsKwh > 0 ? shoulderConsKwh / dayConsKwh : 0
  const batteryDaySavingsRate = retailPeakRate * dayLoadPeakFrac + retailShoulderRate * dayLoadShoulderFrac

  const energySavings =
    (daySelfUse * solarSavingsRate +
      batDayUse * batteryDaySavingsRate +
      batNightUse * retailNightRate) *
    llfMult

  const exportBenefit = exportKwh * weightedFit

  const netCost = Math.max(monthlyFixedCharge, costBeforeSolar - (energySavings + exportBenefit))

  // Battery flat hours
  const totalBatteryCons = remainingDayConsKwh + nightConsKwh
  const batteryNeedDaily = totalBatteryCons / DAYS_IN_MONTH
  const deficitRatio = batteryNeedDaily > 0 ? Math.max(0, 1 - dailyStoredKwh / batteryNeedDaily) : 0
  
  const dayNeedRatio = totalBatteryCons > 0 ? remainingDayConsKwh / totalBatteryCons : 0
  const nightNeedRatio = totalBatteryCons > 0 ? nightConsKwh / totalBatteryCons : 0
  const dischargeHours = (dayNeedRatio * 6) + (nightNeedRatio * 10)
  
  const flatHours = isBattery && config.useEss ? Math.round(dischargeHours * deficitRatio * DAYS_IN_MONTH) : 0

  // EV costs
  let monthlyEvCost = 0
  let evCostPer100km = 0
  if (config.useEv) {
    // For 'shoulder' (Free Window) profile, the user charges exclusively
    // during the free window, so use the weekend shoulder rate (post-discount)
    const evRate =
      config.evChargeProfile === 'night'
        ? retailNightRate
        : config.evChargeProfile === 'shoulder'
          ? wkndShoulderRate
          : retailPeakRate
    evCostPer100km = evRate * config.evEfficiency * llfMult
    monthlyEvCost = evRate * monthlyEvKwh * llfMult
  }

  return {
    netCost: Math.round(netCost),
    costWithoutSolar: Math.round(costBeforeSolar),
    solarBenefit: Math.round(energySavings + exportBenefit),
    flatHours,
    solarGenerationKwh: Math.round(potentialGenKwh),
    gridConsumptionKwh: Math.round(gridDayKwh + gridNightKwh),
    evCostPer100km,
    monthlyEvCost,
    totalLoadKwh: Math.round(monthlyConsKwh + monthlyEvKwh),
    selfConsKwh: Math.round(totalSelfConsumption),
    exportKwh: Math.round(exportKwh),
    fixedChargeCost: Math.round(monthlyFixedCharge),
    importCost: Math.round(costBeforeSolar - monthlyFixedCharge),
    exportCredit: Math.round(exportBenefit),
    selfUseSavings: Math.round(energySavings),
  }
}
